import React, { useState, useEffect, useRef } from 'react';
import IconMenuOpen from '../../assets/Icons/IconMenuOpen.png';
import IconTune from '../../assets/Icons/IconTune.png';

const FloatingMenuTab = ({ 
  isOpen = false, 
  onToggle, 
  children, 
  initialPosition = { y: window.innerHeight / 2 - 40 },
  iconType = 'menu' // 'menu' for user sidebars, 'tune' for BrowseTalent filter
}) => {
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [expandedTop, setExpandedTop] = useState(null);
  const [isPressed, setIsPressed] = useState(false);
  const [isClosePressed, setIsClosePressed] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showDragArrows, setShowDragArrows] = useState(false);
  const [hasShownArrows, setHasShownArrows] = useState(false);
  const dragStartY = useRef(0);
  const dragStartPosY = useRef(0);
  const arrowTimeoutRef = useRef(null);
  const contentRef = useRef(null);
  const containerRef = useRef(null);

  // Check if this is the user's first visit
  useEffect(() => {
    const hasSeenHint = localStorage.getItem('floatingTabSimpleHintSeen');
    if (!hasSeenHint && !isOpen) {
      // Show hint after a short delay
      const timer = setTimeout(() => {
        setShowHint(true);
        // Auto-hide hint after 5 seconds
        setTimeout(() => {
          setShowHint(false);
          localStorage.setItem('floatingTabSimpleHintSeen', 'true');
        }, 5000);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Show drag arrows helper function
  const showDragHint = () => {
    if (hasShownArrows || isOpen) return;
    
    // Clear any existing timeout
    if (arrowTimeoutRef.current) {
      clearTimeout(arrowTimeoutRef.current);
    }
    
    setShowDragArrows(true);
    setHasShownArrows(true);
    
    // Hide arrows after 800ms with fade out
    arrowTimeoutRef.current = setTimeout(() => {
      setShowDragArrows(false);
    }, 800);
  };

  // Reset arrow hint state when menu opens/closes or drag starts
  const resetArrowHint = () => {
    if (arrowTimeoutRef.current) {
      clearTimeout(arrowTimeoutRef.current);
    }
    setShowDragArrows(false);
    setHasShownArrows(false);
  };

  // Simple dragging logic
  const handleMouseDown = (e) => {
    // Only allow dragging when collapsed
    if (isOpen) return;
    
    // Check if clicking on the menu button area (middle 44px of the tab)
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    if (clickX > 12 && clickX < 52) {
      // This is a click on the menu button area, don't start dragging
      return;
    }
    
    // Show drag arrows hint on first click in draggable area
    showDragHint();
    
    // Hide hint when user starts dragging
    if (showHint) {
      setShowHint(false);
      localStorage.setItem('floatingTabSimpleHintSeen', 'true');
    }
    
    setIsDragging(true);
    dragStartY.current = e.clientY;
    dragStartPosY.current = position.y;
    
    // Reset arrow hint when dragging starts
    resetArrowHint();
  };

  const handleTouchStart = (e) => {
    if (isOpen) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const touchX = e.touches[0].clientX - rect.left;
    if (touchX > 12 && touchX < 52) {
      return;
    }
    
    // Show drag arrows hint on first touch in draggable area
    showDragHint();
    
    // Hide hint when user starts dragging
    if (showHint) {
      setShowHint(false);
      localStorage.setItem('floatingTabSimpleHintSeen', 'true');
    }
    
    setIsDragging(true);
    dragStartY.current = e.touches[0].clientY;
    dragStartPosY.current = position.y;
    
    // Reset arrow hint when dragging starts
    resetArrowHint();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const deltaY = e.clientY - dragStartY.current;
      const newY = Math.max(0, Math.min(window.innerHeight - 80, dragStartPosY.current + deltaY));
      setPosition({ y: newY });
    };

    const handleTouchMove = (e) => {
      const deltaY = e.touches[0].clientY - dragStartY.current;
      const newY = Math.max(0, Math.min(window.innerHeight - 80, dragStartPosY.current + deltaY));
      setPosition({ y: newY });
    };

    const handleEnd = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging]);

  // Recalculate expanded position on window resize
  useEffect(() => {
    if (isOpen && expandedTop !== null) {
      const handleResize = () => {
        setExpandedTop(calculateSmartExpansion());
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isOpen, expandedTop, position.y]);

  // Handle scroll isolation when cursor is over the floating menu
  useEffect(() => {
    const handleWheel = (e) => {
      if (!isOpen || !contentRef.current) return;

      const content = contentRef.current;
      const { scrollTop, scrollHeight, clientHeight } = content;

      // Check if we're scrolling up or down
      const scrollingUp = e.deltaY < 0;
      const scrollingDown = e.deltaY > 0;

      // Check if content can scroll in the direction of the wheel event
      const canScrollUp = scrollTop > 0;
      const canScrollDown = scrollTop < scrollHeight - clientHeight;

      // If we can scroll in the direction of the wheel event, prevent page scroll
      if ((scrollingUp && canScrollUp) || (scrollingDown && canScrollDown)) {
        e.preventDefault();
        e.stopPropagation();

        // Manually scroll the content
        content.scrollTop += e.deltaY;
      } else if (!canScrollUp && !canScrollDown) {
        // If content doesn't need to scroll (fits in container), prevent page scroll
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const container = containerRef.current;
    if (container && isOpen) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [isOpen]);

  // Cleanup arrow timeout on unmount
  useEffect(() => {
    return () => {
      if (arrowTimeoutRef.current) {
        clearTimeout(arrowTimeoutRef.current);
      }
    };
  }, []);

  // Calculate smart expansion position
  const calculateSmartExpansion = () => {
    const viewportHeight = window.innerHeight;
    const tabTop = position.y;
    const tabHeight = 90;
    const expandedHeightCalc = Math.min(viewportHeight * 0.95, 900); // Increased to match expandedHeight

    let newTop = tabTop;

    // Determine expansion direction based on position
    const topThreshold = viewportHeight * 0.2;
    const bottomThreshold = viewportHeight * 0.8;

    // Use smaller margin (10px instead of 20px) to maximize space
    const margin = 10;

    if (tabTop < topThreshold) {
      // Near top - expand downward
      newTop = Math.min(tabTop, margin);
    } else if (tabTop > bottomThreshold) {
      // Near bottom - expand upward
      newTop = Math.max(viewportHeight - expandedHeightCalc - margin, margin);
    } else {
      // Middle - center the expansion
      newTop = Math.max(margin, Math.min(
        tabTop - (expandedHeightCalc - tabHeight) / 2,
        viewportHeight - expandedHeightCalc - margin
      ));
    }

    return newTop;
  };

  // Simple menu button click handler
  const handleMenuClick = (e) => {
    e.stopPropagation();
    
    // Hide hint when user clicks menu
    if (showHint) {
      setShowHint(false);
      localStorage.setItem('floatingTabSimpleHintSeen', 'true');
    }
    
    // Reset arrow hints when menu opens/closes
    resetArrowHint();
    
    if (!isOpen) {
      // Calculate position before opening
      setExpandedTop(calculateSmartExpansion());
    } else {
      // Reset position when closing
      setExpandedTop(null);
    }
    if (onToggle) {
      onToggle();
    }
  };

  // Calculate expanded height - maximized for mobile while maintaining safe margins
  const expandedHeight = Math.min(window.innerHeight * 0.95, 900); // Increased from 0.80 to 0.95 and max from 720 to 900

  // Base styles with arrow/chevron design
  const tabStyle = {
    position: 'fixed',
    left: 0,
    top: isOpen && expandedTop !== null ? `${expandedTop}px` : `${position.y}px`,
    width: isOpen ? '360px' : '65px', // Increased from 300px to 360px for expanded state
    height: isOpen ? `${expandedHeight}px` : '72px',  // Reduced by 10% from 80px
    background: 'linear-gradient(126deg, rgba(1, 162, 241, 1), rgba(57, 58, 62, 1))',
    WebkitBackground: 'linear-gradient(126deg, rgba(1, 162, 241, 1), rgba(57, 58, 62, 1))',
    MozBackground: 'linear-gradient(126deg, rgba(1, 162, 241, 1), rgba(57, 58, 62, 1))',
    borderRadius: isOpen ? '0 20px 20px 0' : '0',
    clipPath: isOpen ? 'none' : 'polygon(0 0, 50px 0, 65px 50%, 50px 100%, 0 100%)',
    transition: 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    zIndex: 99999,
    display: 'flex',
    flexDirection: isOpen ? 'column' : 'row',
    alignItems: isOpen ? 'stretch' : 'center',
    justifyContent: isOpen ? 'flex-start' : 'center',
    padding: isOpen ? '20px' : '0',
    paddingLeft: isOpen ? '20px' : '0',  // No padding when collapsed
    cursor: isDragging ? 'grabbing' : (isOpen ? 'default' : 'grab'),
    userSelect: 'none',
    boxShadow: 'none',
    filter: 'none',
  };

  // Container for collapsed state content
  const collapsedContentStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0px',  // No gap between hamburger and dots
    paddingLeft: '5px',  // Position content from left edge
    paddingRight: '10px',  // Small right padding
  };

  // Menu button style - mobile optimized
  const menuButtonStyle = {
    width: '44px',  // Larger touch target
    height: '44px',  // iOS recommended minimum
    background: isPressed ? 'rgba(248, 80, 8, 0.15)' : 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '6px',  // Reduced padding to accommodate larger icon
    position: 'relative',
    left: '-2px',  // Fixed offset for collapsed state
    transform: isPressed ? 'scale(0.95)' : 'scale(1)',
    transition: 'transform 0.15s ease',  // Remove left transition
    flexShrink: 0,
    borderRadius: '50%',
    WebkitTapHighlightColor: 'transparent',  // Remove iOS tap highlight
  };

  // Determine which icon to use based on iconType
  const iconSrc = iconType === 'tune' ? IconTune : IconMenuOpen;
  const shouldFlip = iconType === 'menu'; // Only flip menu icon to face right
  
  // Icon style with conditional transform
  const iconStyle = {
    width: '32px',
    height: '32px',
    minWidth: '32px',  // Prevent shrinking
    minHeight: '32px', // Prevent shrinking
    maxWidth: '32px',  // Prevent growing
    maxHeight: '32px', // Prevent growing
    pointerEvents: 'none',
    opacity: isPressed ? 0.7 : 1,
    transition: 'all 0.15s ease',
    filter: 'brightness(0) invert(1)', // Makes the icon white
    flexShrink: 0,     // Prevent flex shrinking
    objectFit: 'contain', // Maintain aspect ratio
    transform: shouldFlip ? 'scaleX(-1)' : 'none', // Flip menu icon horizontally
  };

  // Grip dots style
  const dotsContainerStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: '3px',
  };

  const dotColumnStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  };

  const dotStyle = {
    width: '3.5px',
    height: '3.5px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  };

  // Close button container style
  const closeButtonContainerStyle = {
    position: 'absolute',
    top: '5px',
    right: '5px',
    zIndex: 1,
  };

  // Content container style
  const contentStyle = {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    opacity: isOpen ? 1 : 0,
    transition: 'opacity 0.3s ease',
    transitionDelay: isOpen ? '0.2s' : '0s',
    maxHeight: '100%',  // Full height now that header is removed
    paddingTop: '10px',  // Add some top padding for content
    WebkitOverflowScrolling: 'touch',  // Smooth scrolling on iOS
  };

  // Hint tooltip style
  const hintStyle = {
    position: 'absolute',
    left: '75px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(248, 80, 8, 0.95)',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '12px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    whiteSpace: 'nowrap',
    opacity: showHint ? 1 : 0,
    pointerEvents: 'none',
    transition: 'opacity 0.3s ease',
    boxShadow: 'none',
    zIndex: 100000,
  };

  // Drag arrow styles
  const dragArrowBaseStyle = {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '16px',
    color: '#01a2f1',
    pointerEvents: 'none',
    opacity: showDragArrows ? 1 : 0,
    transition: 'opacity 0.3s ease',
    zIndex: 100000,
    textShadow: '0 1px 3px rgba(0,0,0,0.3)',
  };

  const upArrowStyle = {
    ...dragArrowBaseStyle,
    bottom: '82px', // 10px gap from 72px tab height
  };

  const downArrowStyle = {
    ...dragArrowBaseStyle,
    top: '82px', // 10px gap from 72px tab height
  };

  return (
    <div
      ref={containerRef}
      style={tabStyle}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {!isOpen ? (
        // Collapsed state with arrow/chevron design
        <div style={collapsedContentStyle}>
          <button
            style={menuButtonStyle}
            onClick={handleMenuClick}
            onMouseDown={(e) => {
              e.stopPropagation();
              setIsPressed(true);
            }}
            onMouseUp={() => setIsPressed(false)}
            onMouseLeave={() => setIsPressed(false)}
            onTouchStart={(e) => {
              e.stopPropagation();
              setIsPressed(true);
            }}
            onTouchEnd={() => setIsPressed(false)}
            aria-label="Open menu"
          >
            <img 
              src={iconSrc} 
              alt="Menu" 
              style={iconStyle}
            />
          </button>
          
          <div style={dotsContainerStyle}>
            <div style={dotColumnStyle}>
              <div style={dotStyle}></div>
              <div style={dotStyle}></div>
              <div style={dotStyle}></div>
            </div>
            <div style={dotColumnStyle}>
              <div style={dotStyle}></div>
              <div style={dotStyle}></div>
              <div style={dotStyle}></div>
            </div>
          </div>
          
          {/* Hint tooltip */}
          {showHint && (
            <div style={hintStyle}>
              Drag to move • Tap to open
            </div>
          )}
        </div>
      ) : (
        // Expanded state
        <>
          <div style={closeButtonContainerStyle}>
            <button
              onClick={handleMenuClick}
              onMouseDown={() => setIsClosePressed(true)}
              onMouseUp={() => setIsClosePressed(false)}
              onMouseLeave={() => setIsClosePressed(false)}
              onTouchStart={() => setIsClosePressed(true)}
              onTouchEnd={() => setIsClosePressed(false)}
              style={{
                background: isClosePressed ? 'rgba(248, 80, 8, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                border: isClosePressed ? '1px solid #f85008' : '1px solid #01a2f1',
                color: '#ffffff',  // Always white
                fontSize: '18px',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '50%',
                transition: 'all 0.15s ease',
                transform: isClosePressed ? 'scale(0.85)' : 'scale(1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                WebkitTapHighlightColor: 'transparent',
                boxShadow: 'none',  // Removed shadow
              }}
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>
          <div ref={contentRef} style={contentStyle}>
            {children}
          </div>
        </>
      )}
      
      {/* Drag arrows - only show when collapsed */}
      {!isOpen && (
        <>
          <div style={upArrowStyle}>▲</div>
          <div style={downArrowStyle}>▼</div>
        </>
      )}
    </div>
  );
};

export default FloatingMenuTab;